import React from 'react';
import { NavLink, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import { ButtonGroup, GroupProps } from '../group';
import { colors, type ThemeProps } from '../../style';

export interface NavTabButtonProps {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  to?: string | RouteComponentProps['location'];
  componentClass?: React.ElementType;
  exact?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;

  target?: '_blank';
}

const NavTabComponent = ({
  className,
  disabled,
  active,
  to,
  href,
  componentClass: Component = href ? 'a' : to ? NavLink : 'button',
  ...rest
}: NavTabButtonProps) => {
  const isNativeLink = href || to;
  return (
    <Component
      activeClassName={to ? 'active' : undefined}
      to={to}
      href={href}
      className={classNames('navbar-button', className, { disabled, active })}
      disabled={disabled}
      role={isNativeLink ? undefined : 'link'}
      aria-current={active ? 'page' : undefined}
      {...rest}
    />
  );
};

const actionColor = (props: { theme: ThemeProps }) => props.theme.mainColor || colors.action;

const NavTabButton = styled(NavTabComponent)<NavTabButtonProps>`
  border: none;
  background: transparent;
  border-bottom: 4px solid transparent;
  color: ${colors.gray800};
  font-weight: 400;
  font-family: Roboto;
  display: flex;
  align-items: center;
  font-size: 1rem;
  padding: 8px;
  text-decoration: none;
  &:not([disabled]),
  &:not(.disabled) {
    cursor: pointer;
  }

  &:hover {
    background: ${colors.gray100};
  }

  &.active {
    font-weight: 500;
    color: ${colors.darkBlue};
    border-bottom: 4px solid ${actionColor};
  }

  &.disabled {
    color: ${colors.gray600};
  }

  &:focus-visible {
    border: 1px solid ${actionColor};
  }
`;

export const NavTabGroup = (props: GroupProps & { ariaLabel: string; className?: string }): React.JSX.Element => {
  return (
    <ButtonGroup
      role={'navigation'}
      sideItemsMargin={'sm'}
      bottomItemMargin={'none'}
      aria-label={props.ariaLabel}
      height={'100%'}
      {...props}
    />
  );
};

export default NavTabButton;
